import React, { useState, useEffect, useMemo } from "react";
import {
    Button,
    Grid,
    MenuItem,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    styled,
    IconButton,
    InputAdornment,
    Divider,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    FormLabel,
    Typography
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import { H2, H6 } from "components/Typography";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import apiService from "Network/apiService";
import CityDropdown from "../Members/CityDropdown";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AppModal from "components/AppModal";
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useToast } from "OtherReusableFunctionality/ToastContext";
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Paper from '@mui/material/Paper';

const bloodGroups = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const genderOptions = ["Male", "Female", "Other"];
const memberCategories = ["president", "vicePresident", "treasurer", "member"];

const UpdateMemberModal = ({ open, onClose, member, onSuccess }) => {
    const toast = useToast();
    const [businessCategories, setBusinessCategories] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [selectedBusinessCategory, setSelectedBusinessCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const businessCategoriesResponse = await apiService.get(API_ENDPOINTS.GET_business_category);
                if (businessCategoriesResponse?.success && businessCategoriesResponse?.data) {
                    setBusinessCategories(businessCategoriesResponse.data);
                    if (member?.business_category) {
                        const categoryIds = member.business_category.map(cat => cat.id);
                        setFieldValue('business_category', categoryIds);
                    }
                }

                const chaptersResponse = await apiService.get(API_ENDPOINTS.GET_all_chapters);
                if (chaptersResponse?.success && chaptersResponse?.data) {
                    setChapters(chaptersResponse.data);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to fetch required data");
            }
        };

        if (open) {
            fetchData();
        }
    }, [open, member]);

    const validationSchema = Yup.object().shape({
        business_category: Yup.array()
            .min(1, "At least one Business Category is Required")
            .required("Business Category is Required"),
        member_category: Yup.string().required("Member Category is Required"),
        full_name: Yup.string().required("Full Name is Required"),
        company_name: Yup.string().required("Company Name is Required"),
        company_turnover: Yup.string().required("Company Turnover is Required"),
        contact_no: Yup.string()
            .matches(/^[0-9]{10}$/, "Invalid phone number")
            .required("Contact No is Required"),
        email: Yup.string().email("Invalid email").required("Email is Required"),
        username: Yup.string()
            .min(4, "Username must be at least 4 characters")
            .required("Username is Required"),
        password: Yup.string()
            .min(8, "Password must be at least 8 characters")
            .required("Password is Required"),
        dob: Yup.date().required("Date of Birth is Required"),
        blood_group: Yup.string().required("Blood Group is Required"),
        gender: Yup.string().required("Gender is Required"),
        address: Yup.string().required("Address is Required"),
        city: Yup.string().required("City is Required"),
        chapter: Yup.number().required("Chapter is Required"),
        
    });

    const formik = useFormik({
        initialValues: {
            business_category: member?.business_category || [],
            member_category: member?.member_category || "member",
            full_name: member?.full_name || "",
            company_name: member?.company_name || "",
            company_turnover: member?.company_turnover || "",
            email: member?.email || "",
            contact_no: member?.contact_no || "",
            username: "",
            password: "",
            dob: member?.dob ? new Date(member.dob) : null,
            blood_group: member?.blood_group || "",
            is_married: member?.is_married ? member?.is_married : false,
            anniversary_date: member?.anniversary_date ? new Date(member.anniversary_date) : null,
            gender: member?.gender || "",
            address: member?.address || "",
            city: member?.city || "",
            expectations_from_chapter: member?.expectations_from_chapter || "",
            information_from: member?.information_from || "",
            photo_maratha_validity: member?.photo_maratha_validity || null,
            id: member?.id || null,
            chapter: member?.chapter || "",
            registration_date: member?.registration_date ? new Date(member.registration_date) : null,
            expiry_date: member?.expiry_date ? new Date(member.expiry_date) : null,
        },
        
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            // if (!values.registration_date) {
            //     toast.showError("Please select a Registration Date");
            //     return;
            // }
            // if (!values.expiry_date) {
            //     toast.showError("Please select an Expiry Date");
            //     return;
            // }
            setIsLoading(true);
            try {
                const formData = new FormData();
                Object.keys(values).forEach(key => {
                    if (values[key] !== null && values[key] !== undefined) {
                        if (key === 'dob' || key === 'anniversary_date') {
                            formData.append(key, values[key].toISOString().split('T')[0]);
                        } else if (key === 'is_married') {
                            formData.append(key, values[key] ? "True" : "False");
                        } else if (key === 'business_category') {
                            formData.append('business_category', values[key].join(','));
                        } else if (key === 'registration_date' || key === 'expiry_date') {
                            formData.append(key, new Date(values[key]).toISOString().split('T')[0]);
                        } else {
                            formData.append(key, values[key]);
                        }
                    }
                });

                const response = await apiService.put(
                    API_ENDPOINTS.PUT_accept_self_registered_members,
                    formData,
                    "token",
                    'multipart/form-data'
                );

                if (response?.success) {
                    toast.showSuccess("Member updated and accepted successfully");
                    onSuccess();
                }
            } catch (error) {
                console.error("Error updating member:", error);
                if (error?.message === "Username already exists.") {
                    toast.showError("Username already exists. Please choose a different username.");
                } else {
                    toast.showError("Failed to update member");
                }
            } finally {
                setIsLoading(false);
            }
        }
    });

    const { values, errors, touched, handleChange, handleSubmit, setFieldValue } = formik;

    const filteredCategories = useMemo(() => {
        return businessCategories.filter(category =>
            category.category_name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [businessCategories, searchQuery]);

    return (
        <AppModal
            open={open}
            handleClose={onClose}
            sx={{
                "& .MuiPaper-root": {
                    backgroundColor: "background.paper",
                    maxWidth: 800,
                    width: "100%",
                    maxHeight: "100vh",
                    overflow: "hidden"
                }
            }}
        >
            <Box sx={{
                height: "70vh",
                overflow: "auto",
                p: 3,
                color: "text.primary"
            }}>
                <H2 mb={3}>Update Self-Registered Member</H2>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "primary.main" }}>Chapter Assignment</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="chapter"
                                        label="Select Chapter*"
                                        value={values.chapter}
                                        onChange={handleChange}
                                        error={Boolean(errors.chapter && touched.chapter)}
                                        helperText={touched.chapter && errors.chapter}
                                    >
                                        {chapters.map((chapter) => (
                                            <MenuItem key={chapter.id} value={chapter.id}>
                                                {chapter.name}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>
                              
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "primary.main" }}>Membership Dates</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Registration Date"
                                            value={values.registration_date}
                                            onChange={(newValue) => {
                                                setFieldValue('registration_date', newValue);
                                            }}
                                            openTo="day"
                                            views={['year', 'month', 'day']}
                                            renderInput={(params) => (
                                                <AppTextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Expiry Date"
                                            value={values.expiry_date}
                                            onChange={(newValue) => {
                                                setFieldValue('expiry_date', newValue);
                                            }}
                                            openTo="day"
                                            views={['year', 'month', 'day']}
                                            renderInput={(params) => (
                                                <AppTextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "primary.main" }}>Admin Set Credentials</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="username"
                                        label="Username*"
                                        value={values.username}
                                        onChange={handleChange}
                                        error={Boolean(errors.username && touched.username)}
                                        helperText={touched.username && errors.username}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="password"
                                        label="Password*"
                                        type={showPassword ? 'text' : 'password'}
                                        value={values.password}
                                        onChange={handleChange}
                                        error={Boolean(errors.password && touched.password)}
                                        helperText={touched.password && errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Business Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="business_category"
                                        label="Business Category*"
                                        value={values.business_category || []}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            const newValue = typeof value === 'string' ? value.split(',') : value;
                                            setFieldValue('business_category', newValue);
                                        }}
                                        error={Boolean(errors.business_category && touched.business_category)}
                                        helperText={touched.business_category && errors.business_category}
                                        SelectProps={{
                                            multiple: true,
                                            open: menuOpen,
                                            onOpen: () => setMenuOpen(true),
                                            onClose: () => setMenuOpen(false),
                                            renderValue: (selected) => {
                                                if (!selected?.length) return '';
                                                return businessCategories
                                                    .filter(cat => selected.includes(cat.id))
                                                    .map(cat => cat.category_name)
                                                    .join(', ');
                                            },
                                            input: <OutlinedInput label="Business Category*" />,
                                            MenuProps: {
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 400
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <Box sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                                            <Paper sx={{ p: 1, m: 1, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                                                    <InputBase
                                                        placeholder="Search categories..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{ flex: 1 }}
                                                    />
                                                </Box>
                                            </Paper>
                                        </Box>
                                        <Box sx={{ 
                                            maxHeight: 300,
                                            overflow: 'auto',
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: '#f1f1f1',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: '#888',
                                                borderRadius: '4px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                background: '#555',
                                            },
                                        }}>
                                            {filteredCategories.map((category) => (
                                                <MenuItem 
                                                    key={category.id} 
                                                    value={category.id}
                                                    onClick={() => {
                                                        const currentValues = values.business_category || [];
                                                        const newValues = currentValues.includes(category.id)
                                                            ? currentValues.filter(id => id !== category.id)
                                                            : [...currentValues, category.id];
                                                        setFieldValue('business_category', newValues);
                                                    }}
                                                >
                                                    <Checkbox 
                                                        checked={values.business_category?.includes(category.id) || false}
                                                        color="primary"
                                                    />
                                                    <ListItemText primary={category.category_name} />
                                                </MenuItem>
                                            ))}
                                        </Box>
                                    </AppTextField>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="member_category"
                                        label="Member Category*"
                                        value={values.member_category}
                                        onChange={handleChange}
                                        error={Boolean(errors.member_category && touched.member_category)}
                                        helperText={touched.member_category && errors.member_category}
                                    >
                                        {memberCategories.map((category) => (
                                            <MenuItem key={category} value={category}>
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Personal Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="full_name"
                                        label="Full Name*"
                                        value={values.full_name}
                                        onChange={handleChange}
                                        error={Boolean(errors.full_name && touched.full_name)}
                                        helperText={touched.full_name && errors.full_name}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="company_name"
                                        label="Company Name*"
                                        value={values.company_name}
                                        onChange={handleChange}
                                        error={Boolean(errors.company_name && touched.company_name)}
                                        helperText={touched.company_name && errors.company_name}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            label="Date of Birth*"
                                            value={values.dob}
                                            onChange={(newValue) => setFieldValue("dob", newValue)}
                                            renderInput={(params) => (
                                                <AppTextField
                                                    {...params}
                                                    fullWidth
                                                    size="small"
                                                    error={Boolean(errors.dob && touched.dob)}
                                                    helperText={touched.dob && errors.dob}
                                                />
                                            )}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="blood_group"
                                        label="Blood Group*"
                                        value={values.blood_group}
                                        onChange={handleChange}
                                        error={Boolean(errors.blood_group && touched.blood_group)}
                                        helperText={touched.blood_group && errors.blood_group}
                                    >
                                        {bloodGroups.map((group) => (
                                            <MenuItem key={group} value={group}>
                                                {group}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>Marital Status</FormLabel>
                                        <RadioGroup
                                            row
                                            name="is_married"
                                            value={values.is_married ? "married" : "unmarried"}
                                            onChange={(e) => {
                                                setFieldValue("is_married", e.target.value === "married");
                                                if (e.target.value === "unmarried") {
                                                    setFieldValue("anniversary_date", null);
                                                }
                                            }}
                                        >
                                            <FormControlLabel 
                                                value="married" 
                                                control={<Radio />} 
                                                label="Married" 
                                            />
                                            <FormControlLabel 
                                                value="unmarried" 
                                                control={<Radio />} 
                                                label="Unmarried" 
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                {values.is_married && (
                                    <Grid item xs={12} md={6}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                label="Anniversary Date"
                                                value={values.anniversary_date}
                                                onChange={(newValue) => setFieldValue("anniversary_date", newValue)}
                                                renderInput={(params) => (
                                                    <AppTextField
                                                        {...params}
                                                        fullWidth
                                                        size="small"
                                                        error={Boolean(errors.anniversary_date && touched.anniversary_date)}
                                                        helperText={touched.anniversary_date && errors.anniversary_date}
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                )}

                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        select
                                        fullWidth
                                        size="small"
                                        name="gender"
                                        label="Gender*"
                                        value={values.gender}
                                        onChange={handleChange}
                                        error={Boolean(errors.gender && touched.gender)}
                                        helperText={touched.gender && errors.gender}
                                    >
                                        {genderOptions.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </AppTextField>
                                </Grid>

                                <Grid item xs={12}>
                                    <AppTextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        size="small"
                                        name="address"
                                        label="Address*"
                                        value={values.address}
                                        onChange={handleChange}
                                        error={Boolean(errors.address && touched.address)}
                                        helperText={touched.address && errors.address}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <CityDropdown
                                        value={values.city}
                                        onChange={(value) => setFieldValue("city", value)}
                                        error={Boolean(errors.city && touched.city)}
                                        helperText={touched.city && errors.city}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Contact Information</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="contact_no"
                                        label="Contact No*"
                                        value={values.contact_no}
                                        onChange={handleChange}
                                        error={Boolean(errors.contact_no && touched.contact_no)}
                                        helperText={touched.contact_no && errors.contact_no}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AppTextField
                                        fullWidth
                                        size="small"
                                        name="email"
                                        label="Email*"
                                        value={values.email}
                                        onChange={handleChange}
                                        error={Boolean(errors.email && touched.email)}
                                        helperText={touched.email && errors.email}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <H6 mb={2} sx={{ color: "text.secondary" }}>Documents</H6>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    {member?.photo_maratha_validity && (
                                        <Box mb={2}>
                                            <H6>Current Document:</H6>
                                            <a 
                                                href={member.photo_maratha_validity} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                            >
                                                View Document
                                            </a>
                                        </Box>
                                    )}
                                    <AppTextField
                                        type="file"
                                        fullWidth
                                        size="small"
                                        name="photo_maratha_validity"
                                        onChange={(event) => {
                                            setFieldValue("photo_maratha_validity", event.currentTarget.files[0]);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <FlexBox justifyContent="flex-end" gap={2} mt={4}>
                                <Button
                                    variant="outlined"
                                    onClick={onClose}
                                    sx={{ minWidth: 120 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ minWidth: 120 }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? <CircularProgress size={24} /> : "Update & Accept"}
                                </Button>
                            </FlexBox>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </AppModal>
    );
};

export default UpdateMemberModal; 